import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useSimulateScenario, useHistoricalEpisodes } from '@/hooks/api/useScenarios'
import { useRegimeTemplates, REGIME_TEMPLATES, type RegimeTemplate } from '@/hooks/api/useRegimes'
import { useAssets } from '@/hooks/api/useAssets'
import { usePortfolio } from '@/hooks/api/usePortfolio'
import { useScenarioForm } from '@/hooks/useScenarioForm'
import { ModeSelector } from '@/components/scenario/ModeSelector'
import { HistoricalReplayForm } from '@/components/scenario/HistoricalReplayForm'
import { BTCCurveRegimeForm } from '@/components/scenario/BTCCurveRegimeForm'
import { CustomShocksForm } from '@/components/scenario/CustomShocksForm'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Loader2, Play, AlertCircle, Info } from 'lucide-react'
import type { ScenarioMode } from '@/types/api'

// =============================================================================
// Route Component
// =============================================================================

function ScenariosPage() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Data fetching
  const { data: portfolio } = usePortfolio()
  const { data: episodes = [] } = useHistoricalEpisodes()
  const { data: templates = REGIME_TEMPLATES } = useRegimeTemplates()
  const { data: assets = [] } = useAssets()
  const simulateMutation = useSimulateScenario()

  // Form state management
  const form = useScenarioForm(portfolio?.id ?? '')

  // Handle mode change
  const handleModeChange = (mode: ScenarioMode) => {
    form.setMode(mode)
  }

  // Handle submission
  const handleSubmit = async () => {
    if (!form.isValid) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const request = form.buildRequest()
      if (!request) {
        throw new Error('Failed to build valid request')
      }

      const result = await simulateMutation.mutateAsync(request)
      
      // Store result and navigate to results page
      sessionStorage.setItem('scenarioResult', JSON.stringify(result))
      navigate({ to: '/results' })
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Simulation failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Scenario Configuration</h1>
          <Badge variant="outline" className="capitalize text-sm">
            {form.mode.toLowerCase().replace(/_/g, ' ')}
          </Badge>
        </div>
        <p className="text-base text-muted-foreground">
          Configure stress testing scenarios and parameters for portfolio analysis.
        </p>
      </div>

      <Separator className="my-6" />

      {/* Mode Selector */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">1. Select Scenario Mode</h2>
          <p className="text-sm text-muted-foreground">Choose how you want to model market conditions</p>
        </div>
        <ModeSelector value={form.mode} onChange={handleModeChange} />
      </section>

      {/* Mode-Specific Forms */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">2. Configure Parameters</h2>
          <p className="text-sm text-muted-foreground">Set specific values for your chosen scenario mode</p>
        </div>
        
        {form.mode === 'HISTORICAL_REPLAY' && (
          <HistoricalReplayForm
            episodes={episodes}
            selectedEpisodeId={form.historicalForm.episodeId}
            replayMode={form.historicalForm.replayMode}
            onEpisodeChange={form.historicalForm.setEpisodeId}
            onReplayModeChange={form.historicalForm.setReplayMode}
          />
        )}

        {form.mode === 'BTC_CURVE_REGIME' && (
          <BTCCurveRegimeForm
            btcPricePath={form.btcCurveForm.btcPricePath}
            betaMode={form.btcCurveForm.betaMode}
            selectedTemplateId={form.btcCurveForm.selectedTemplateId}
            templateOverrides={form.btcCurveForm.templateOverrides}
            templates={templates as RegimeTemplate[]}
            onBtcPricePathChange={form.btcCurveForm.setBtcPricePath}
            onBetaModeChange={form.btcCurveForm.setBetaMode}
            onTemplateSelect={form.btcCurveForm.setSelectedTemplateId}
            onTemplateOverrideChange={form.btcCurveForm.setTemplateOverride}
          />
        )}

        {form.mode === 'CUSTOM_SHOCKS' && (
          <CustomShocksForm
            assets={assets}
            assetShocks={form.customForm.assetShocks}
            onAssetShockChange={form.customForm.setAssetShock}
            onResetAll={form.customForm.resetAllShocks}
          />
        )}
      </section>

      {/* Validation Errors */}
      {form.errors.length > 0 && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-destructive mb-2">Please fix the following issues:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                  {form.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submission Error */}
      {submitError && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-destructive mb-1">Simulation Failed</h4>
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Section */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">3. Run Simulation</h2>
          <p className="text-sm text-muted-foreground">Execute the stress test with your configured parameters</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Info className="h-4 w-4" />
                <span className="text-sm">
                  {form.isValid 
                    ? 'All parameters configured. Ready to run simulation.' 
                    : 'Complete all required fields above to run simulation'}
                </span>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={!form.isValid || isSubmitting}
                size="lg"
                className="ml-4"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run Simulation
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Help Text */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-3">About Scenario Modes</h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Historical Replay:</strong> Apply observed historical market moves to your current portfolio.
              Choose from episodes like COVID crash, Rate Hike Cycle, or Crypto Contagion.
            </p>
            <p>
              <strong className="text-foreground">BTC Curve + Regime:</strong> Input a BTC price path and select a TradFi regime template.
              Crypto assets priced via beta to BTC, TradFi via regime assumptions.
            </p>
            <p>
              <strong className="text-foreground">Custom Shocks:</strong> Specify explicit percentage moves for individual assets.
              Coherence checker validates statistical plausibility.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Route Definition
// =============================================================================

export const Route = createFileRoute('/scenarios')({
  component: ScenariosPage,
})
